import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill, formatCurrency, formatDate } from '@/components/live';
import { UrdInvoice, type UrdTrade } from '@/screens/parts/UrdInvoice';

interface Row extends UrdTrade { invoice_state: 'none' | 'generated' | 'uploaded'; payment_status: string }

const STATE_LABEL: Record<Row['invoice_state'], string> = { none: 'No invoice yet', generated: 'URD generated', uploaded: 'Uploaded' };

/**
 * Every farmer sale and its invoice (owner, 2026-09-27: "Sale Invoices"): generate the farmer's
 * self-declared (URD) invoice, or upload the one they have. A sale is recorded on Farmer sales;
 * here only its invoice is kept.
 */
export default function SaleInvoicesScreen() {
  return (
    <RegisterScreen<Row>
      canAdd={false} addLabel="" exportName="sale-invoices" recordNoun="invoice"
      filters={{ seller_type: 'farmer', transaction_type: 'sale' }}
      orderBy={{ column: 'transaction_date', ascending: false }}
      recordLabel={(row) => `invoice for ${row.trade_code ?? 'sale'}`}
      screenKey="plot.harvest"
      searchColumns={['invoice_no', 'trade_code', 'seller_display', 'buyer_display']}
      secondary={{ sum: (row) => row.total_amount, unit: '', money: true }}
      subtitle="The invoice for each farmer sale: a self-declared URD invoice generated from the sale, or the farmer's own, uploaded."
      table="ops.trade_transactions" title="Sale invoices" view="trade_transactions"
      columns={[
        { key: 'invoice_no', label: 'Invoice', render: (row) => (
          <span className="cell-stack"><strong>{row.invoice_no ?? '--'}</strong>
            <small className="muted">{row.invoice_date ? formatDate(row.invoice_date) : ''}</small></span>
        ) },
        { key: 'trade_code', label: 'Sale', render: (row) => (
          <span className="cell-stack"><strong>{row.trade_code ?? '--'}</strong><small className="muted">{formatDate(row.transaction_date)}</small></span>
        ) },
        { key: 'seller_display', label: 'Farmer', render: (row) => row.seller_display ?? '--' },
        { key: 'buyer_display', label: 'Buyer', render: (row) => row.buyer_display ?? '--' },
        { key: 'total_amount', label: 'Amount', align: 'right', render: (row) => formatCurrency(row.total_amount) },
        { key: 'invoice_state', label: 'Invoice', render: (row) => <StatusPill value={STATE_LABEL[row.invoice_state]} /> },
        { key: 'payment_status', label: 'Payment', render: (row) => <StatusPill value={row.payment_status} /> },
      ]}
      stats={[
        { key: 'none', label: 'No invoice yet', tone: 'error', match: (row) => row.invoice_state === 'none' },
        { key: 'generated', label: 'URD generated', tone: 'ok', match: (row) => row.invoice_state === 'generated' },
        { key: 'uploaded', label: 'Uploaded', tone: 'info', match: (row) => row.invoice_state === 'uploaded' },
      ]}
      fields={[
        { key: 'invoice_no', label: 'Invoice no.', type: 'text', placeholder: 'Blank: URD/ and the sale\'s reference when generated' },
        { key: 'invoice_date', label: 'Invoice date', type: 'date' },
        { key: 'invoice_document_id', label: 'Upload the farmer\'s invoice', type: 'file',
          hint: 'Or generate a self-declared invoice below, from the sale itself.' },
      ]}
    >
      {(row) => <UrdInvoice trade={row} />}
    </RegisterScreen>
  );
}
